/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

/**
 * Types for the dynamic data-point collection config (`PropertyConfig.dcfg`).
 * The wire contract is schemas/rtd-data-collection-config.v1.json, kept in
 * lock-step with the management-api models that generate the config.
 */

export type DcfgDataType = 'STRING' | 'INTEGER' | 'FLOAT' | 'BOOLEAN' | 'ARRAY';

export type DcfgTransform =
  | { op: 'first' }
  | { op: 'split'; delimiter?: string }
  | { op: 'lowercase' }
  | { op: 'nth'; index: number };

export type GlobalJsSource = {
  type: 'global_js';
  property_name: string;
};

export type CookieSource = {
  type: 'cookie';
  property_name: string;
};

export type LocalStorageSource = {
  type: 'local_storage';
  property_name: string;
  parse_json?: boolean;
  json_path?: string;
};

export type PbjsEidSource = {
  type: 'pbjs_eid';
  eid_source: string;
};

export type AdserverKvSource = {
  type: 'adserver_kv';
  key: string;
};

export type ArrayFindSource = {
  type: 'array_find';
  array_path: string;
  match: { path: string; equals: string };
  value_path: string;
};

export type JsonLdSource = {
  type: 'json_ld';
  schema_type: string;
  value_path: string;
};

export type MetaTagSource = {
  type: 'meta_tag';
  attribute: 'property' | 'name';
  value: string;
};

export type DataPointSource =
  | GlobalJsSource
  | CookieSource
  | LocalStorageSource
  | PbjsEidSource
  | AdserverKvSource
  | ArrayFindSource
  | JsonLdSource
  | MetaTagSource;

/**
 * Reserved data-point key identifying the property's main user id. The point
 * keyed this way (at most one, enforced server-side) ships as the single
 * top-level `uid`, never in `xd`.
 */
export const MAIN_USER_ID_KEY = 'uid';

/**
 * Reserved data-point key for publisher cohorts / user segment IDs. A point
 * keyed this way whose collected value is an array ships as the top-level
 * `pcids`, overriding the vendor-derived cohorts (`publisher.cohort.ids`),
 * never in `xd`. A non-array value is ignored.
 */
export const PUBLISHER_COHORTS_KEY = 'pcids';

export type DataPoint = {
  key: string;
  /** Coercion type. Current wire name; supersedes `data_type`. */
  type?: DcfgDataType;
  /** @deprecated Legacy wire name for `type`; removed in a subsequent build. */
  data_type?: DcfgDataType;
  transforms?: DcfgTransform[];
  source: DataPointSource;
};

export type Dcfg = {
  v: 1;
  points: DataPoint[];
};

export type XdValue = string | string[] | number | boolean;

export type CollectionResult = {
  xd: Record<string, XdValue>;
  /** Resolved value of the point keyed `uid`, if any; ships as the top-level `uid`. */
  mainUserId?: string;
  /**
   * Resolved array value of the point keyed `pcids`, if any; overrides the
   * vendor-derived top-level `pcids`.
   */
  pcids?: string[];
};
